import { addCommasToNumber } from '@/utils/formatters';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper.ts';
import { getJiraProjects } from '@/voc/api/jira.api';
import {
    formatAppsForDownload,
    formatDescriptionForDownload,
    formatLabelsForDownload,
    formatLinkedIdeasForDownload,
    formatPortalsForDownload,
    formatProductAreasForDownload,
    formatStatusForDownload,
} from '@/voc/helpers/common.helper';
import { PerSubscriptionUserStorage } from '@utils/storage';
import debounce from 'lodash/debounce';

export function getAvailableColumns ({
    store,
    type,
    hasSegmentFlag,
    isMultiApp,
    revenueLabel,
    getFeatureFlag,
    hasProductLinePurchased,
}) {
    return [
        {
            prop: 'checkbox',
            type: 'checkbox',
            fixed: true,
            allowReorder: false,
            downloadable: false,
            visible: true,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
        {
            prop: 'source',
            csvProp: 'sourceName',
            slot: 'source',
            title: 'source',
            label: 'Source',
            minWidth: 60,
            width: 150,
            sortable: false,
            fixed: true,
            visible: true,
            formatter: (row) => row.sourceName || row.source,
            availability: {
                tableTypes: ['feedback'],
            },
            allowReorder: false,
        },
        {
            prop: 'vocItemTitle',
            slot: 'title',
            label: 'Title',
            sortable: true,
            minWidth: 290,
            fixed: true,
            visible: true,
            showOverflow: false,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
        {
            prop: 'vocItemDescription',
            slot: 'description',
            label: 'Description',
            sortable: false,
            minWidth: 350,
            width: 600,
            visible: true,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
            showOverflow: false,
            formatter: (row) => formatDescriptionForDownload(row.formData),
        },
        {
            prop: 'status.name',
            label: 'Status',
            slot: 'statusVoc',
            sortable: true,
            visible: true,
            minWidth: 140,
            width: 222,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
            formatter: (row) => formatStatusForDownload(store, row.status?.id),
        },
        {
            prop: 'associatedItemsExpanded',
            label: 'Linked ideas',
            slot: 'linkedIdeas',
            sortField: 'associatedItemIds',
            sortable: true,
            visible: true,
            minWidth: 300,
            width: 300,
            availability: {
                tableTypes: ['feedback'],
            },
            formatter: (row) => formatLinkedIdeasForDownload(row.associatedItemsExpanded),
        },
        {
            prop: 'voteStrength',
            label: 'Importance',
            minWidth: 140,
            width: 200,
            sortable: true,
            visible: true,
            availability: {
                tableTypes: ['feedback'],
            },
        },
        {
            prop: 'account.name',
            label: 'Account',
            slot: 'account',
            minWidth: 140,
            sortable: true,
            visible: true,
            availability: {
                tableTypes: ['feedback'],
            },
        },
        {
            prop: 'account.id',
            label: 'Account ID',
            minWidth: 140,
            sortable: false,
            visible: true,
            availability: {
                tableTypes: ['feedback'],
            },
        },
        {
            prop: 'onBehalfOf.username',
            label: 'Visitor',
            slot: 'visitor',
            minWidth: 140,
            sortable: true,
            visible: true,
            availability: {
                tableTypes: ['feedback'],
            },
        },
        {
            prop: 'effort',
            label: 'Effort',
            slot: 'effort',
            sortable: true,
            width: 150,
            minWidth: 120,
            visible: true,
            availability: {
                tableTypes: ['idea'],
            },
        },
        {
            prop: 'impact',
            label: 'Impact',
            slot: 'impact',
            sortable: true,
            width: 150,
            minWidth: 120,
            visible: true,
            availability: {
                tableTypes: ['idea'],
            },
        },
        {
            prop: 'labels',
            label: 'Labels',
            slot: 'vocLabels',
            visible: true,
            sortable: true,
            minWidth: 284,
            width: 284,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
            formatter: (row) => formatLabelsForDownload(store, row.labelIds),
        },
        {
            prop: 'apps',
            label: 'Apps',
            slot: 'app',
            sortable: true,
            minWidth: 284,
            width: 284,
            visible: true,
            availability: {
                tableTypes: ['idea', 'feedback'],
                multiApp: true,
            },
            formatter: (row) => formatAppsForDownload(store, row.appIds),
        },
        {
            prop: 'productAreas',
            label: 'Product areas',
            slot: 'group',
            sortable: true,
            minWidth: 284,
            width: 284,
            visible: true,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
            formatter: (row) => formatProductAreasForDownload(store, row.productAreaIds),
        },
        {
            prop: 'createdBy.username',
            label: 'Created by',
            sortable: true,
            visible: true,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
            formatter: (row) => row.createdBy?.username ?? row.createdBy?.user?.username,
        },
        {
            prop: 'createdAt',
            label: 'Created time',
            sortable: true,
            visible: false,
            formatter: (row) => moment(row.createdAt).format(DATE_FORMAT.full),
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
        {
            prop: 'ownedByUser.username',
            label: 'Assignee',
            slot: 'assignee',
            sortable: true,
            minWidth: 284,
            width: 284,
            visible: true,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
        {
            prop: 'lastUpdatedBy.username',
            label: 'Last updated by',
            sortable: true,
            visible: false,
            formatter: (row) =>
                (row.lastUpdatedBy?.username === '--system-user--' ? 'Automation rule' : row.lastUpdatedBy?.username),
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
        {
            prop: 'lastUpdatedAt',
            label: 'Last updated time',
            sortable: true,
            visible: true,
            formatter: (row) => moment(row.lastUpdatedAt).format(DATE_FORMAT.full),
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
        {
            prop: 'voteCount',
            label: 'Evidence',
            slot: 'votes',
            sortable: true,
            visible: true,
            availability: {
                tableTypes: ['idea'],
            },
        },
        {
            prop: 'accountVoteCount',
            label: 'Account votes',
            sortable: true,
            visible: true,
            availability: {
                tableTypes: ['idea'],
            },
        },
        {
            prop: 'portalIds',
            label: 'Portals',
            slot: 'portal',
            minWidth: 200,
            width: 200,
            sortable: true,
            visible: false,
            availability: {
                tableTypes: ['idea'],
            },
            formatter: (row) => formatPortalsForDownload(store, row.portalIds),
        },
        {
            prop: 'revenue',
            label: revenueLabel,
            minWidth: 140,
            sortable: true,
            visible: true,
            sortBy: 'revenue',
            formatter: (row) => {
                if (!row.revenue) {
                    return '--';
                }

                return addCommasToNumber(row.revenue);
            },
            availability: {
                tableTypes: ['idea'],
            },
        },
        {
            prop: 'npsScore',
            label: 'NPS rating',
            minWidth: 150,
            sortable: true,
            availability: {
                tableTypes: ['feedback'],
                productLine: 'nps',
            },
        },
        {
            type: 'actions',
            prop: 'actions',
            width: 80,
            downloadable: false,
            availability: {
                tableTypes: ['idea', 'feedback'],
            },
        },
    ].filter(
        (column) =>
            column?.availability?.tableTypes?.includes(type) &&
            (!column?.availability?.segmentFlag || hasSegmentFlag(column.availability.segmentFlag)) &&
            (!column?.availability?.subscriptionFlag || getFeatureFlag(column.availability.subscriptionFlag)) &&
            (!column?.availability?.productLine || hasProductLinePurchased(column.availability.productLine)) &&
            (column?.availability?.multiApp === undefined || column.availability.multiApp === isMultiApp),
    );
}

export function getVisibleColumns ({ type, storedColumns = [] }) {
    if (storedColumns?.length) {
        const columns = storedColumns.filter((column) => column !== 'checkbox' && column !== 'actions');

        return ['checkbox', ...columns, 'actions'];
    }

    const defaultVisibleColumns = {
        feedback: [
            'checkbox',
            'source',
            'vocItemTitle',
            'account.name',
            'onBehalfOf.username',
            'vocItemDescription',
            'status.name',
            'associatedItemsExpanded',
            'voteStrength',
            'labels',
            'apps',
            'productAreas',
            'createdBy.username',
            'ownedByUser.username',
            'lastUpdatedAt',
            'actions',
        ],
        idea: [
            'checkbox',
            'vocItemTitle',
            'status.name',
            'labels',
            'effort',
            'impact',
            'apps',
            'productAreas',
            'createdBy.username',
            'ownedByUser.username',
            'lastUpdatedAt',
            'voteCount',
            'accountVoteCount',
            'revenue',
            'actions',
        ],
    };

    return defaultVisibleColumns[type];
}

export const storeFilters = debounce(storeFiltersDebounced, 1000);
function storeFiltersDebounced ({ filters }) {
    PerSubscriptionUserStorage.set('vocListExpansionFilters', filters);
}

export function isJiraConnected (errorMessage) {
    return (
        errorMessage !== 'jira integration instance not found' &&
        errorMessage !== 'jira integration not configured' &&
        errorMessage !== 'jira integration not enabled'
    );
}

export async function getAllJiraProjects ({ startAt = 0 } = { startAt: 0 }) {
    const maxResults = 50;
    const jiraProjects = [];

    const { values, isLast } = await getJiraProjects({ startAt, maxResults });
    jiraProjects.push(...values);

    if (!isLast) {
        jiraProjects.push(...(await getAllJiraProjects({ startAt: startAt + maxResults })));
    }

    return jiraProjects;
}
